import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

export const createFileFromBase64 = (base64Content, fileName) => {
    try {
        if (!base64Content) return null;
        const binaryString = window.atob(base64Content.replace(/\s/g, ''));
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return new File([bytes], fileName, { type: 'text/plain' });
    } catch (e) {
        console.error("File creation failed:", e);
        return null;
    }
};

export const triggerZipDownload = (zipBase64, fileName) => {
    try {
        if (!zipBase64) return false;
        const cleanBase64 = zipBase64.replace(/\s/g, '');
        const binaryString = window.atob(cleanBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName.replace('.txt', '')}_FVU.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        return true;
    } catch (e) {
        console.error("Zip Download failed:", e);
        return false;
    }
};

export const triggerFvuDownload = (fvuBase64, fileName) => {
    try {
        if (!fvuBase64) return false;

        // Ensure clean base64
        const cleanBase64 = fvuBase64.replace(/\s/g, '');

        // Decode
        const binaryString = window.atob(cleanBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        // Create Blob
        // FVU file is essentially text/plain but with specific extension
        const blob = new Blob([bytes], { type: 'text/plain' });

        // Create Link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Set filename (ensure .fvu extension)
        const baseName = fileName.replace('.txt', '');
        link.download = `${baseName}.fvu`;

        // Trigger
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Cleanup
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        return true;
    } catch (e) {
        console.error("FVU Download failed:", e);
        return false;
    }
};

// Helper to download via API stream
export const downloadGeneratedFile = async (filePath) => {
    try {
        toast.info("Preparing download...");
        const response = await axios.get(`/api/download-generated-file?path=${encodeURIComponent(filePath)}`, {
            responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Extract filename from filePath
        const fileName = filePath.split(/[\\/]/).pop() || 'downloaded-file';
        link.setAttribute('download', fileName);

        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Download started");
    } catch (err) {
        console.error("Download failed:", err);
        toast.error("Download failed. File might be missing or inaccessible.");
    }
};


export const handleValidationSuccess = (response, fileName) => {
    // Handle both Axios response and direct data object
    const data = response.data || response;

    // Auto-download Zip if provided (legacy)
    if (data.zipBase64) {
        triggerZipDownload(data.zipBase64, fileName);
    }

    const outputDir = data.outputDir;
    const message = data.message || 'Validation Completed Successfully!';

    toast.success(
        <div className="flex flex-col gap-2">
            <span className="font-semibold">{message}</span>
            <div className="flex flex-row gap-2 mt-1">
                {outputDir && (
                    <button
                        onClick={() => {
                            axios.post('/api/open-folder', { folderPath: outputDir })
                                .catch(() => toast.error("Could not open folder"));
                        }}
                        className="text-xs bg-white text-green-700 font-bold py-1 px-2 rounded shadow hover:bg-green-50 flex items-center justify-center border border-green-200"
                    >
                        📂 Open Folder
                    </button>
                )}

                {/* Prefer path-based download if available */}
                {data.zipPath ? (
                    <button
                        onClick={() => downloadGeneratedFile(data.zipPath)}
                        className="text-xs bg-white text-blue-700 font-bold py-1 px-2 rounded shadow hover:bg-blue-50 flex items-center justify-center border border-blue-200"
                    >
                        ⬇️ Download Zip
                    </button>
                ) : (data.zipBase64 && (
                    <button
                        onClick={() => triggerZipDownload(data.zipBase64, fileName)}
                        className="text-xs bg-white text-blue-700 font-bold py-1 px-2 rounded shadow hover:bg-blue-50 flex items-center justify-center border border-blue-200"
                    >
                        ⬇️ Download Zip (B64)
                    </button>
                ))}

                {data.fvuPath && (
                    <button
                        onClick={() => downloadGeneratedFile(data.fvuPath)}
                        className="text-xs bg-white text-indigo-700 font-bold py-1 px-2 rounded shadow hover:bg-indigo-50 flex items-center justify-center border border-indigo-200"
                    >
                        📄 Download FVU
                    </button>
                )}
            </div>
            {data.logs && (
                <details className="mt-1 text-[10px] text-white/90">
                    <summary className="cursor-pointer">View Logs</summary>
                    <pre className="mt-1 bg-black/20 p-1 rounded overflow-x-auto max-h-20">
                        {data.logs.substring(0, 300)}...
                    </pre>
                </details>
            )}
            {/* Show Error File content if available/relevant? Usually if success=true, there is no error file, but maybe warnings? */}
        </div>
        , {
            autoClose: 10000, // Longer timeout for user to read
            closeOnClick: false,
            draggable: false
        }
    );
};

export const downloadCsiFileApi = async (credentials, dateRange, financialYear, quarter) => {
    try {
        const response = await axios.post('/api/download-csi', {
            credentials,
            dateRange,
            financialYear,
            quarter,
            headless: false
        });

        if (response.data.success && response.data.csiContent) {
            const file = createFileFromBase64(response.data.csiContent, response.data.fileName);
            return { success: true, file, fileName: response.data.fileName };
        }
        return { success: false, error: response.data.error || 'Download failed' };
    } catch (e) {
        return { success: false, error: e.response?.data?.error || e.message };
    }
};

export const validateTdsFileApi = async (payload) => {
    try {
        const response = await axios.post('/api/validate-tds-file', payload);
        return response.data;
    } catch (e) {
        throw e;
    }
};

export const extractProfileDataApi = async (credentials) => {
    try {
        const response = await axios.post('/api/extract-profile-data', {
            credentials,
            headless: false
        });

        if (response.data.success) {
            return { success: true, data: response.data.data };
        }
        return { success: false, error: response.data.error || 'Extraction failed' };
    } catch (e) {
        return { success: false, error: e.response?.data?.error || e.message };
    }
};
